import { useTranslation } from 'react-i18next';

import type { SkeletonGate } from '~/shared/lib/useSkeletonGate';
import { EmptyState } from '~/shared/ui/EmptyState';
import { DumbbellMarkIcon } from '~/shared/ui/EmptyStateIcons';
import inlineButton from '~/shared/ui/inlineTextButton.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { MatrixView } from '../model/formaMatrix';

import styles from './Forma.module.css';

const SKELETON_ROWS = 6;
const WEEKS = 5;

interface FormaMatrixProps {
  readonly view: MatrixView;
  readonly gate: SkeletonGate;
  readonly failed: boolean;
  readonly onRetry: () => void;
  readonly onOpen: (exerciseId: number) => void;
}

/** Rows are movements, columns the last five ISO weeks, a cell that week's top set. */
export function FormaMatrix({ view, gate, failed, onRetry, onOpen }: FormaMatrixProps) {
  const { t } = useTranslation();
  const weeks = gate.waiting ? Array.from({ length: WEEKS }, () => '') : view.weeks;

  return (
    <>
      <div
        aria-busy={gate.waiting}
        aria-label={t('web:forma.matrix.label')}
        className={styles.matrixCard}
        role="table"
      >
        <div role="rowgroup">
          <div className={`${String(styles.matrixGrid)} ${String(styles.matrixHead)}`} role="row">
            <span role="columnheader">{t('web:forma.matrix.exercise')}</span>
            {weeks.map((week, index) => (
              <span className={styles.sessionCentre} key={index} role="columnheader">
                {week}
              </span>
            ))}
            <span className={styles.sessionFigure} role="columnheader">
              {t('web:forma.matrix.change')}
            </span>
          </div>
        </div>
        <div className={`${String(styles.matrixRows)} ${gate.reveal}`} role="rowgroup">
          {gate.waiting ? (
            <>
              <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
              {gate.skeleton
                ? Array.from({ length: SKELETON_ROWS }, (_, row) => (
                    <div className={styles.matrixGrid} key={row} role="row">
                      <i className={`${String(skel.bar)} ${String(styles.skelLine)}`} role="cell" />
                      {Array.from({ length: WEEKS }, (__, cell) => (
                        <i className={`${String(skel.bar)} ${String(styles.skelMatrixCell)}`} key={cell} role="cell" />
                      ))}
                      <span role="cell" />
                    </div>
                  ))
                : null}
            </>
          ) : failed ? (
            <p className={styles.listNotice}>
              {t('web:forma.matrix.failed')}{' '}
              <button className={inlineButton.inlineTextButton} onClick={onRetry} type="button">
                {t('web:clientDetail.states.retry')}
              </button>
            </p>
          ) : view.rows.length === 0 ? (
            <EmptyState mark={<DumbbellMarkIcon />} title={t('web:forma.matrix.empty')} tone="neutral" />
          ) : (
            view.rows.map((row) => (
              <div className={`${String(styles.matrixGrid)} ${String(styles.matrixRow)}`} key={row.exerciseId} role="row">
                <span role="rowheader">
                  <button
                    aria-label={
                      row.unread
                        ? `${t('web:forma.matrix.open', { name: row.name })}, ${t('web:forma.list.unread')}`
                        : t('web:forma.matrix.open', { name: row.name })
                    }
                    className={styles.matrixName}
                    onClick={() => {
                      onOpen(row.exerciseId);
                    }}
                    type="button"
                  >
                    {row.name}
                    {row.unread ? <span aria-hidden="true" className={styles.unreadDot} /> : null}
                  </button>
                </span>
                {row.cells.map((cell, index) =>
                  cell === null ? (
                    <span className={`${String(styles.matrixCell)} ${String(styles.matrixCellEmpty)}`} key={index} role="cell">
                      <span className={hidden.srOnly}>{t('web:forma.matrix.noSet')}</span>
                    </span>
                  ) : (
                    <span className={styles.matrixCell} key={index} role="cell">
                      {cell.top}
                      {cell.sub === null ? null : <small className={styles.matrixSub}>{cell.sub}</small>}
                    </span>
                  ),
                )}
                <span
                  className={`${String(styles.sessionFigure)} ${String(styles.matrixChange)} ${row.changeIsGood ? String(styles.matrixChangeGood) : ''}`}
                  role="cell"
                >
                  {row.change ?? '·'}
                  {row.changeOf === null ? null : <small className={styles.matrixSub}>{row.changeOf}</small>}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
      {gate.waiting || failed || view.limitNote === null ? null : (
        <p className={styles.matrixNote}>{view.limitNote}</p>
      )}
    </>
  );
}
